import { Box, Flex, Icon, Text, useDisclosure } from "@chakra-ui/react";
import LogRocket from "logrocket";
import React from "react";
import { HiOutlineSquare3Stack3D } from "react-icons/hi2";

import {
  Button,
  GreenhouseRatingButton,
  LeverRatingButton,
  RouterLink,
} from "../../../components";
import useCurrentBreakpoint from "../../../hooks/useCurrentBreakpoint";
import { useIsSmallScreen } from "../../../hooks/useIsSmallScreen";
import { formatRelativeDate, MS_PER_DAY } from "../../../utils/datetime";
import { useSendGAEvent } from "../../../utils/googleAnalytics";
import { FeatureName, ScheduledInterviewListItemFragment } from "../../graphql";
import useCurrentUser from "../../hooks/useCurrentUser";
import { useHasFeatureAccess } from "../../hooks/useHasFeatureAccess";
import InterviewMenu from "../Menu/InterviewMenu";
import ScheduledInterviewMenu from "../Menu/ScheduledInterviewMenu";
import ScoringButton from "../ScoringButton/ScoringButton";
import InterviewChapterMobile from "./InterviewChapterMobile";
import InterviewChapters from "./InterviewChapters";
import ReactionsGroup from "./ReactionsGroup";
import RecordingMessage from "./RecordingMessage";
import { ScheduledInterviewLaunchButton } from "./ScheduledInterviewLaunchButton";
import Thumbnail from "./Thumbnail";
import { CandidateCall, InterviewMenuOptions } from "./types";
import { getCallName, getScheduledCallName } from "./utils";

interface BaseListItemProps {
  listPosition: number;
  currentUserNotesOnly: boolean;
}

interface CallListItemProps extends BaseListItemProps {
  call: CandidateCall;
  scheduledInterview?: never;
  showVisibility?: boolean;
}

interface ScheduledInterviewListItemProps extends BaseListItemProps {
  scheduledInterview: ScheduledInterviewListItemFragment;
  call?: never;
  setActiveHighlight?: never;
  showVisibility?: never;
}

type InterviewListItemProps =
  | CallListItemProps
  | ScheduledInterviewListItemProps;

const InterviewListItem: React.FC<InterviewListItemProps> = ({
  call,
  scheduledInterview,
  listPosition,
  currentUserNotesOnly,
  showVisibility,
}) => {
  const currentUser = useCurrentUser();
  const sendGAEvent = useSendGAEvent();
  const isSmallScreen = useIsSmallScreen();
  const currentBreakpoint = useCurrentBreakpoint();
  const isSmallishScreen = isSmallScreen || currentBreakpoint === "lg";

  const participants = scheduledInterview
    ? scheduledInterview.scheduledInterviewers.map((s) => {
        if (s.user && s.user.id === currentUser.id) {
          return "You";
        }
        return s.user ? s.user.fullName : s.name || "name unavailable";
      })
    : [];
  const { allowAiNotes } = currentUser.organization;
  const showChapters =
    useHasFeatureAccess(FeatureName.AiChapterTitles) && allowAiNotes;
  const chaptersDisclosure = useDisclosure({
    onOpen: () => {
      sendGAEvent("view_interview_chapters", "candidate");
      LogRocket.track("view_interview_chapters");
    },
  });
  const isPlayable =
    call && (!!call.streamableAudio?.url || !!call.streamableVideo?.url);

  const onLinkClick =
    (from: string): (() => void) =>
    () =>
      call ? sendGAEvent("view_interview", "candidate", from) : undefined;

  return (
    <Box
      position="relative"
      borderRadius="xl"
      transition="0.3s background"
      mb={7}
    >
      <Flex direction={isSmallishScreen ? "column" : "row"} position="relative">
        <Box
          width={isSmallishScreen ? "100%" : "207px"}
          maxW="600px"
          transition="width 0.3s"
        >
          <RouterLink
            to={call ? `/interview/${call.id}` : "#"}
            onClick={onLinkClick("thumbnail")}
          >
            <Box minWidth="207px">
              <Thumbnail
                size="auto"
                showUpcomingOverlay={!!scheduledInterview}
                imageUrl={
                  call
                    ? call.thumbnailImageUrl
                    : "/static/images/upcoming-interview-background.png"
                }
                audioOnly={
                  call &&
                  !!call.streamableAudio?.url &&
                  !call.streamableVideo?.url
                }
                isPlayable={isPlayable}
                time={call?.duration}
                fullVideoVariant
                timeFontSize={["lg", null, "xl"]}
                borderRadius="xl"
                visibility={showVisibility ? call?.visibility : undefined}
              />
            </Box>
          </RouterLink>
        </Box>
        <Flex
          direction="column"
          align="start"
          flex="1"
          ml={isSmallishScreen ? "0" : "6"}
          mr={isSmallishScreen ? "0" : "4"}
          gap={isSmallishScreen ? "0" : "2"}
        >
          <Box
            fontSize="sm"
            color="gray.600"
            fontWeight="500"
            mt={isSmallishScreen ? "4" : "0"}
            justifyContent="space-between"
            width="100%"
            display="flex"
          >
            <Flex display="inline-block" fontSize="sm">
              {call
                ? formatRelativeDate(call.startTime ?? call.createdAt)
                : formatRelativeDate(
                    scheduledInterview.scheduledStart,
                    21 * MS_PER_DAY
                  )}
            </Flex>
            <Flex display="inline-block">
              {call && isPlayable && isSmallishScreen && (
                <InterviewMenu
                  call={call}
                  positionId={call.positionId}
                  menuOptions={[
                    InterviewMenuOptions.SHARE,
                    InterviewMenuOptions.DELETE,
                  ]}
                  listPosition={listPosition}
                  buttonSize="xs"
                  placeMenuInside
                />
              )}
            </Flex>
          </Box>
          <Box fontWeight="600">
            <RouterLink
              to={call ? `/interview/${call.id}` : "#"}
              onClick={onLinkClick("interview_title")}
              _hover={{ textDecoration: "none" }}
              fontSize="md"
              fontWeight="600"
              color="gray.800"
            >
              {call
                ? getCallName(call)
                : getScheduledCallName(scheduledInterview)}
            </RouterLink>
          </Box>
          <Flex direction={["column", null, "row"]} align="start">
            {call && call.interviewers.length > 0 && (
              <Box mr="3" fontSize="sm" color="gray.800">
                {call?.interviewers.map((i) => i.fullName).join(", ")}
              </Box>
            )}
            {participants.length > 0 && (
              <Text
                className="content"
                fontSize="sm"
                marginTop="1"
                noOfLines={2}
              >
                {participants.join(", ")}
              </Text>
            )}
            {call?.isInterviewer && (
              <Flex mt={{ base: "1", lg: "0" }} mb={3} gap="2">
                {call.greenhouseScorecardLinkWithId && (
                  <GreenhouseRatingButton
                    greenhouseScorecardLink={call.greenhouseScorecardLinkWithId}
                    rating={call.greenhouseUserRating}
                    variant={!isSmallishScreen ? "ghost" : undefined}
                  />
                )}
                {call.greenhouseUserRating &&
                  call.leverInterviewLinkWithId &&
                  " "}
                {call.leverInterviewLinkWithId && (
                  <LeverRatingButton
                    leverInterviewLink={call.leverInterviewLinkWithId}
                    variant={!isSmallishScreen ? "ghost" : undefined}
                  />
                )}
                {isSmallishScreen && <ScoringButton call={call} />}
              </Flex>
            )}
          </Flex>
          {!isSmallishScreen && call && <ScoringButton call={call} />}
          <Flex flexDir={["row", "row", "column", "column", "row"]}>
            {call && (
              <Box whiteSpace="nowrap" display="flex">
                <ReactionsGroup
                  callId={call.id}
                  notes={call.notes}
                  currentUserNotesOnly={currentUserNotesOnly}
                  borderColor="gray.50"
                />
              </Box>
            )}
            {call?.notes.length === 0 && <Box w={[2, 2, 0, 2]} />}
            {showChapters && call && (
              <ChapterToggle
                disclosure={chaptersDisclosure}
                isSmallScreen={isSmallishScreen}
              />
            )}
          </Flex>
        </Flex>
        <Flex flex="0" flexDir="column">
          {call && isPlayable && !isSmallishScreen && (
            <InterviewMenu
              call={call}
              positionId={call.positionId}
              menuOptions={[
                InterviewMenuOptions.VIEW,
                InterviewMenuOptions.SHARE,
                InterviewMenuOptions.COPY_AI_NOTES,
                InterviewMenuOptions.DELETE,
              ]}
              listPosition={listPosition}
              placeMenuInside
            />
          )}
          {scheduledInterview && !isSmallishScreen && (
            <Flex marginTop="3" alignItems="center">
              {scheduledInterview.isInterviewer && (
                <ScheduledInterviewLaunchButton
                  scheduledInterview={scheduledInterview}
                />
              )}
              <ScheduledInterviewMenu
                scheduledInterview={scheduledInterview}
                currentUser={currentUser}
                placeMenuInside
              />
            </Flex>
          )}
          {scheduledInterview?.isInterviewer && isSmallishScreen && (
            <Flex marginTop="3" alignItems="center" width="100%">
              <ScheduledInterviewLaunchButton
                scheduledInterview={scheduledInterview}
                h="10"
                w="100%"
                flex="1"
              />
            </Flex>
          )}
          {scheduledInterview && (
            <RecordingMessage
              scheduledInterview={scheduledInterview}
              marginTop="3"
              alignSelf="flex-start"
            />
          )}
        </Flex>
      </Flex>

      {showChapters && call && isSmallScreen && chaptersDisclosure.isOpen && (
        <InterviewChapterMobile
          call={call}
          isOpen={chaptersDisclosure.isOpen}
          onClose={chaptersDisclosure.onClose}
          thumbnailUrl={call.thumbnailImageUrl}
        />
      )}

      {showChapters && call && !isSmallScreen && (
        <InterviewChapters
          call={call}
          isOpen={chaptersDisclosure.isOpen}
          onClose={chaptersDisclosure.onClose}
          thumbnailUrl={call.thumbnailImageUrl}
        />
      )}
    </Box>
  );
};

type ChapterToggleProps = {
  disclosure: ReturnType<typeof useDisclosure>;
  isSmallScreen: boolean;
};

const ChapterToggle: React.FC<ChapterToggleProps> = ({
  disclosure,
  isSmallScreen,
}) => {
  return (
    <Button
      aria-label="Interview Summary"
      data-tour-id="candidate-page-interview-summary"
      size="sm"
      variant="link"
      fontWeight={500}
      onClick={disclosure.onToggle}
      textTransform="unset"
      iconSpacing={1}
      ml={[6, 6, 0, 0, 4]}
      rightIcon={<Icon as={HiOutlineSquare3Stack3D} boxSize="5" />}
    >
      Interview summary
    </Button>
  );
};

export default InterviewListItem;
